import { useGetList } from "ra-core";
import {
  CheckCircle2,
  CreditCard,
  ExternalLink,
  RefreshCcw,
  XCircle,
  Wallet,
  Repeat,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { Payment, Subscription } from "../types";
import { formatCurrency } from "../finance/treasuryFormat";

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function PaymentIcon({ type }: { type: string }) {
  if (type.includes("refund"))
    return <RefreshCcw className="size-4 text-amber-500" />;
  if (type.includes("failed"))
    return <XCircle className="size-4 text-rose-500" />;
  if (type.includes("paid") || type.includes("succeeded"))
    return <CheckCircle2 className="size-4 text-emerald-500" />;
  return <CreditCard className="size-4 text-muted-foreground" />;
}

function SubscriptionStatusBadge({ status }: { status: string }) {
  const tone =
    status === "active" || status === "trialing"
      ? "bg-emerald-500/15 text-emerald-600"
      : status === "past_due" || status === "unpaid"
        ? "bg-amber-500/15 text-amber-600"
        : status === "canceled" || status === "incomplete_expired"
          ? "bg-muted text-muted-foreground"
          : "bg-muted text-foreground";
  return (
    <Badge variant="outline" className={`font-normal ${tone}`}>
      {status}
    </Badge>
  );
}

export const CompanyPayments = ({ companyId }: { companyId: number }) => {
  const { data: subscriptions, isPending: isSubsPending } =
    useGetList<Subscription>("subscriptions", {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "created_at", order: "DESC" },
      filter: { company_id: companyId },
    });

  const { data: payments, isPending: isPayPending } = useGetList<Payment>(
    "payments",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "occurred_at", order: "DESC" },
      filter: { company_id: companyId },
    },
  );

  const isPending = isSubsPending || isPayPending;

  if (isPending) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        Chargement des paiements…
      </div>
    );
  }

  const hasSubs = (subscriptions?.length ?? 0) > 0;
  const hasPayments = (payments?.length ?? 0) > 0;

  if (!hasSubs && !hasPayments) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Aucun paiement Stripe rattaché à cette entreprise.
          <div className="mt-1 text-xs">
            Renseigne le Stripe customer ID dans la fiche pour commencer.
          </div>
        </CardContent>
      </Card>
    );
  }

  // KPIs: lifetime value (sum of successful charges/invoices net of refunds) +
  // contribution MRR from active subscriptions.
  const ltvByCcy: Record<string, number> = {};
  for (const p of payments ?? []) {
    if (
      p.type.includes("succeeded") ||
      p.type === "invoice_paid" ||
      p.type === "charge_succeeded" ||
      p.type === "charge_refunded"
    ) {
      const net = (p.amount ?? 0) - (p.amount_refunded ?? 0);
      ltvByCcy[p.currency] = (ltvByCcy[p.currency] ?? 0) + net;
    }
  }
  const mrrByCcy: Record<string, number> = {};
  for (const sub of subscriptions ?? []) {
    if (
      (sub.status === "active" || sub.status === "trialing") &&
      sub.amount != null
    ) {
      const ccy = sub.currency ?? "eur";
      const interval = sub.recurring_interval;
      const monthly =
        interval === "year"
          ? Math.round(sub.amount / 12)
          : interval === "week"
            ? sub.amount * 4
            : interval === "day"
              ? sub.amount * 30
              : sub.amount;
      mrrByCcy[ccy] = (mrrByCcy[ccy] ?? 0) + monthly;
    }
  }

  const fmtByCcy = (byCcy: Record<string, number>) => {
    const entries = Object.entries(byCcy);
    if (entries.length === 0) return null;
    return entries.map(([ccy, c]) => formatCurrency(c, ccy)).join(" + ");
  };

  const ltvLabel = fmtByCcy(ltvByCcy);
  const mrrLabel = fmtByCcy(mrrByCcy);

  return (
    <div className="flex flex-col gap-5 pt-2">
      {(ltvLabel || mrrLabel) && (
        <section className="flex flex-wrap gap-2">
          {ltvLabel && (
            <div className="flex flex-col gap-1 px-3 py-2 rounded-md border bg-card min-w-[140px]">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Wallet className="h-3.5 w-3.5" />
                Lifetime value
              </div>
              <div className="text-base font-semibold tabular-nums">
                {ltvLabel}
              </div>
            </div>
          )}
          {mrrLabel && (
            <div className="flex flex-col gap-1 px-3 py-2 rounded-md border bg-card min-w-[140px]">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Repeat className="h-3.5 w-3.5" />
                MRR contribué
              </div>
              <div className="text-base font-semibold tabular-nums">
                {mrrLabel}
              </div>
            </div>
          )}
        </section>
      )}
      {hasSubs && (
        <section>
          <h6 className="mb-2 text-sm font-semibold">Abonnements</h6>
          <div className="flex flex-col gap-2">
            {subscriptions!.map((sub) => (
              <Card key={sub.id} className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {sub.product_name ?? "Abonnement"}
                      <SubscriptionStatusBadge status={sub.status} />
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {formatCurrency(sub.amount, sub.currency ?? "eur")}
                      {sub.recurring_interval
                        ? ` / ${sub.recurring_interval}`
                        : ""}
                      {" · "}
                      Depuis {formatDate(sub.started_at ?? sub.created_at)}
                    </div>
                    {sub.current_period_end && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Période en cours jusqu'au{" "}
                        {formatDate(sub.current_period_end)}
                        {sub.cancel_at_period_end
                          ? " · résiliation programmée"
                          : ""}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {hasPayments && (
        <section>
          <h6 className="mb-2 text-sm font-semibold">
            Historique des paiements
          </h6>
          <div className="flex flex-col divide-y divide-border rounded-md border">
            {payments!.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 px-3 py-2 text-sm"
              >
                <PaymentIcon type={p.type} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">
                    {p.description ??
                      p.invoice_number ??
                      p.type.replace(/_/g, " ")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(p.occurred_at)}
                    {p.status ? ` · ${p.status}` : ""}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-semibold tabular-nums">
                    {formatCurrency(p.amount, p.currency)}
                  </div>
                  {p.amount_refunded > 0 && (
                    <div className="text-[11px] text-amber-600">
                      remb. {formatCurrency(p.amount_refunded, p.currency)}
                    </div>
                  )}
                </div>
                {(p.hosted_invoice_url || p.receipt_url) && (
                  <a
                    href={p.hosted_invoice_url ?? p.receipt_url ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    title="Ouvrir dans Stripe"
                  >
                    <ExternalLink className="size-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
